/**
 * Saving files the browser can't name on its own (T-173). A blob: URL has no
 * filename, and a Supabase signed URL without a download name saves as the
 * storage key's last segment ("fr.pdf"), so every Download goes through here
 * with an explicit name — normally the one the backend sends in
 * Content-Disposition ("Cleo - Contract - Jean Tremblay.pdf").
 */

/**
 * The filename in a Content-Disposition header: the RFC 5987 `filename*`
 * (keeps accents) over the ASCII `filename`, else `fallback`.
 */
export function filenameFromContentDisposition(header: string | null | undefined, fallback: string): string {
  const disposition = header || '';
  const encoded = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition);
  if (encoded) {
    try {
      const name = decodeURIComponent(encoded[1].trim());
      if (name) return name;
    } catch {
      // Malformed percent-encoding — fall through to the plain filename.
    }
  }
  const plain = /filename\s*=\s*"([^"]*)"/i.exec(disposition) || /filename\s*=\s*([^;]+)/i.exec(disposition);
  const name = plain?.[1]?.trim();
  return name || fallback;
}

/** `name` with `.ext` appended unless it already ends with an extension. */
export function withExtension(name: string, ext: string): string {
  return /\.[a-z0-9]{1,5}$/i.test(name) ? name : `${name}.${ext}`;
}

/** The extension (no dot, lower case) of a filename or storage path, or ''. */
export function fileExtension(path: string | null | undefined): string {
  const last = String(path ?? '').split('/').pop() || '';
  const m = /\.([a-z0-9]{1,5})$/i.exec(last);
  return m ? m[1].toLowerCase() : '';
}

/**
 * Name to save a stored document under when the backend sends none: its stored
 * name, with the extension the storage key carries ("Cleo Contract Receipt -
 * 592" has none), else "Cleo - Document".
 */
export function fallbackFilename(doc: { original_filename?: string | null; storage_path?: string | null } | null | undefined): string {
  const ext = fileExtension(doc?.original_filename) || fileExtension(doc?.storage_path) || 'pdf';
  return withExtension((doc?.original_filename || '').trim() || 'Cleo - Document', ext);
}

/** Save a blob under `filename` (an anchor with a download attribute). */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = window.document.createElement('a');
  a.href = url;
  a.download = filename;
  window.document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking synchronously can cancel the download in some browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Save a fetch response under the name its Content-Disposition gives, else `fallback`. */
export async function saveResponse(res: Response, fallback: string): Promise<string> {
  const filename = filenameFromContentDisposition(res.headers.get('Content-Disposition'), fallback);
  saveBlob(await res.blob(), filename);
  return filename;
}
